import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { PublicEventsHubStore } from 'src/app/shared/services/public-events-hub/public-events-hub.state';
import { PublicEventsStore } from 'src/app/shared/services/public-events/state/public-events.state';
import { PublicOnSiteStore } from 'src/app/shared/services/public-on-site/public-on-site.state';
import { AuthenticationStore } from 'src/app/shared/state/authentication/authentication.state';
import { environment } from 'src/environments/environment';

export const auth0LogoutInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthenticationStore);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403 && error.url?.includes(environment.auth0.domain))
        authStore.logout();

      return throwError(() => error);
    })
  );
};

export function whitelistInterceptorFn(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) {
  const allowedDomains = [
    'api-dev.axleaccess.com',
    'api-dev2.axleaccess.com',
    'api-pilot.axleaccess.com',
    'api-demo.axleaccess.com',
    'api-ryan.axleaccess.com',
    'api-spencer.axleaccess.com',
    'api-michael.axleaccess.com',
    'api-craig.axleaccess.com',
    'api-jean.axleaccess.com',
    'api-guns.axleaccess.com',
    'api-goodkind.axleaccess.com',
    'api-piper.axleaccess.com',
    'api-staging.axleaccess.com',
    'api-prod.axleaccess.com',
    'api.axleaccess.com',
    'api-sandbox.axleaccess.com',
    'api-integrations.axleaccess.com',
    'local.axleaccess.com',
    'api-secondary.axleaccess.com',
    '*.axle-be-api-pr.axleaccess.com',
    `https://${environment.auth0.domain}/dbconnections/change_password`
  ];

  if (environment.env === 'local' || environment.env === 'dev') {
    allowedDomains.push('localhost:*');
  }

  const url = req.url.toLowerCase();
  const isAllowed = allowedDomains.some((domain) => matchDomain(url, domain));

  const sessionTokenKey = 'axle_session_token';
  const sessionToken = sessionStorage.getItem(sessionTokenKey) || uuidv4();

  sessionStorage.setItem(sessionTokenKey, sessionToken);

  if (isAllowed) {
    const authReq = req.clone({
      headers: req.headers.set('Axle-Session-Id', sessionToken)
    });

    return next(authReq);
  } else {
    // You can handle the unauthorized request here (e.g., throw an error)
    throw new Error('Unauthorized request to ' + url);
  }
}

export const USE_ON_SITE_TOKEN = new HttpContextToken<boolean>(() => false);

export const publicTokenInterceptorFn: HttpInterceptorFn = (req, next) => {
  const publicEventsStore = inject(PublicEventsStore);
  const eventsHubStore = inject(PublicEventsHubStore);
  const publicOnSiteStore = inject(PublicOnSiteStore);

  let token = null;
  let coordinatingOrgIdentifier = null;
  if (req.context.get(USE_ON_SITE_TOKEN)) {
    token = publicOnSiteStore.token();
  } else {
    switch (getPublicEndpointType(req.url)) {
      case 'hubs':
        token = eventsHubStore.token();
        coordinatingOrgIdentifier = eventsHubStore.coordinatingOrgIdentifier();
        break;
      case 'event':
        token = publicEventsStore.token();
        break;
      case 'on-site':
        token = publicOnSiteStore.token();
        break;
    }
  }

  let headers = {};
  if (token) {
    headers = { 'X-Magic-Token': token };
  }

  if (coordinatingOrgIdentifier) {
    headers = {
      ...headers,
      'X-Events-Hub-Coordinating-Org': coordinatingOrgIdentifier
    };
  }

  if (Object.keys(headers).length) {
    const tokenReq = req.clone({
      setHeaders: headers
    });
    return next(tokenReq);
  }

  return next(req);
};

function matchDomain(url: string, pattern: string): boolean {
  if (!pattern.includes('*')) return url.includes(pattern);

  const regex = new RegExp(
    pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '[a-z0-9-]+')
  );
  return regex.test(url);
}

function getPublicEndpointType(
  url: string
): 'hubs' | 'event' | 'on-site' | null {
  if (url.includes('/public/') && url.includes('/hubs/')) {
    return 'hubs';
  }
  if (url.includes('/public/') && url.includes('/event/')) {
    if (url.includes('/on-site/')) {
      return 'on-site';
    }
    return 'event';
  }
  return null;
}
